import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useCreateFAQ, useUpdateFAQ, useFAQCategories, type FAQItem } from "@/hooks/useFAQs";

interface FAQEditorProps {
  faq: FAQItem | null;
  onClose: () => void;
}

export const FAQEditor = ({ faq, onClose }: FAQEditorProps) => {
  const [question, setQuestion] = useState(faq?.question || "");
  const [answer, setAnswer] = useState(faq?.answer || "");
  const [categoryId, setCategoryId] = useState(faq?.category_id || "");
  const [displayOrder, setDisplayOrder] = useState(faq?.display_order || 0);
  const [isPublished, setIsPublished] = useState(faq?.is_published || false);
  const [keywords, setKeywords] = useState(faq?.keywords?.join(", ") || "");
  const [videoUrl, setVideoUrl] = useState(faq?.video_url || "");

  const { data: categories } = useFAQCategories();
  const createFAQ = useCreateFAQ();
  const updateFAQ = useUpdateFAQ();

  useEffect(() => {
    if (faq) {
      setQuestion(faq.question);
      setAnswer(faq.answer);
      setCategoryId(faq.category_id || "");
      setDisplayOrder(faq.display_order);
      setIsPublished(faq.is_published);
      setVideoUrl(faq.video_url || "");
      setKeywords(faq.keywords?.join(", ") || "");
    }
  }, [faq]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const faqData = {
      question,
      answer,
      category_id: categoryId || null,
      display_order: displayOrder,
      is_published: isPublished,
      keywords: keywords.split(",").map((k) => k.trim()).filter(Boolean),
      related_links: {},
      video_url: videoUrl || null,
    };

    if (faq) {
      await updateFAQ.mutateAsync({ id: faq.id, ...faqData });
    } else {
      await createFAQ.mutateAsync(faqData);
    }

    onClose();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="question">Question *</Label>
        <Input
          id="question"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="e.g., How does iQ Texting work?"
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="answer">Answer *</Label>
        <Textarea
          id="answer"
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
          placeholder="Provide a detailed answer. You can include links and formatting."
          rows={8}
          required
        />
        <p className="text-xs text-muted-foreground">
          Tip: Add links like this: https://example.com - they will be clickable in the answer
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="videoUrl">YouTube Video URL (Optional)</Label>
        <Input
          id="videoUrl"
          value={videoUrl}
          onChange={(e) => setVideoUrl(e.target.value)}
          placeholder="https://www.youtube.com/watch?v=..."
        />
        <p className="text-xs text-muted-foreground">
          Paste a YouTube video URL to embed it with this FAQ
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="category">Category</Label>
        <Select value={categoryId} onValueChange={setCategoryId}>
          <SelectTrigger>
            <SelectValue placeholder="Select a category" />
          </SelectTrigger>
          <SelectContent>
            {categories?.map((category) => (
              <SelectItem key={category.id} value={category.id}>
                {category.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="displayOrder">Display Order</Label>
          <Input
            id="displayOrder"
            type="number"
            value={displayOrder}
            onChange={(e) => setDisplayOrder(parseInt(e.target.value) || 0)}
            min={0}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="keywords">Keywords (comma-separated)</Label>
          <Input
            id="keywords"
            value={keywords}
            onChange={(e) => setKeywords(e.target.value)}
            placeholder="pricing, cost, payment"
          />
        </div>
      </div>

      <div className="flex items-center justify-between p-4 border rounded-lg">
        <div className="space-y-0.5">
          <Label htmlFor="published">Published</Label>
          <p className="text-sm text-muted-foreground">
            Make this FAQ visible on the public page
          </p>
        </div>
        <Switch
          id="published"
          checked={isPublished}
          onCheckedChange={setIsPublished}
        />
      </div>

      <div className="flex justify-end gap-3">
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" disabled={createFAQ.isPending || updateFAQ.isPending}>
          {faq ? "Update FAQ" : "Create FAQ"}
        </Button>
      </div>
    </form>
  );
};
